import React, { useState } from 'react'
import { formatDate, formatDateTime } from '@/utils/dateFormat'
import { Modal } from '@/components/ui/Modal'
import { DataTable } from '@/components/ui/DataTable'

interface MaintenanceWindow {
  id: string
  startDate: string
  endDate: string
  description: string
}

interface DeliveryLog {
  id: string
  recipient: string
  subject: string
  status: 'Delivered' | 'Failed' | 'Pending'
  timestamp: string
}

const NotificationSettings: React.FC = () => {
  const [emailNotificationsEnabled, setEmailNotificationsEnabled] = useState(true)
  const [retentionPeriod, setRetentionPeriod] = useState('90')
  const [maintenanceWindows, setMaintenanceWindows] = useState<MaintenanceWindow[]>([
    { id: '1', startDate: '2026-05-15', endDate: '2026-05-16', description: 'Database maintenance' },
  ])
  const [deliveryLogs, setDeliveryLogs] = useState<DeliveryLog[]>([
    { id: '1', recipient: 'user@example.com', subject: 'User synced to OU', status: 'Delivered', timestamp: new Date().toISOString() },
    { id: '2', recipient: 'admin@example.com', subject: 'Sync failed', status: 'Delivered', timestamp: new Date(Date.now() - 3600000).toISOString() },
  ])
  const [addWindowOpen, setAddWindowOpen] = useState(false)
  const [newWindow, setNewWindow] = useState({ startDate: '', endDate: '', description: '' })

  const handleAddMaintenanceWindow = () => {
    if (newWindow.startDate && newWindow.endDate && newWindow.description) {
      setMaintenanceWindows([
        ...maintenanceWindows,
        { id: String(maintenanceWindows.length + 1), ...newWindow },
      ])
      setNewWindow({ startDate: '', endDate: '', description: '' })
      setAddWindowOpen(false)
    }
  }

  return (
    <div>
      <h2 style={{ fontSize: '18px', fontWeight: 600, margin: '0 0 24px 0', color: 'var(--color-black)' }}>
        Notification settings
      </h2>

      {/* Email notifications toggle */}
      <div style={{ marginBottom: '32px', padding: '16px', border: '1px solid var(--color-light-grey)', borderRadius: '8px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h3 style={{ fontSize: '16px', fontWeight: 600, margin: '0 0 4px 0', color: 'var(--color-black)' }}>
              Email notifications
            </h3>
            <p style={{ margin: 0, fontSize: '14px', color: 'var(--color-dark-grey)' }}>
              Enable or disable email notifications platform-wide
            </p>
          </div>
          <input
            type="checkbox"
            checked={emailNotificationsEnabled}
            onChange={(e) => setEmailNotificationsEnabled(e.target.checked)}
            style={{ width: '20px', height: '20px', cursor: 'pointer' }}
          />
        </div>
      </div>

      {/* Retention period */}
      <div style={{ marginBottom: '32px' }}>
        <h3 style={{ fontSize: '16px', fontWeight: 600, margin: '0 0 16px 0', color: 'var(--color-black)' }}>
          Notification retention
        </h3>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end' }}>
          <div style={{ flex: 1, maxWidth: '200px' }}>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, marginBottom: '4px' }}>
              Retention period (days)
            </label>
            <input
              type="number"
              value={retentionPeriod}
              onChange={(e) => setRetentionPeriod(e.target.value)}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid var(--color-light-grey)',
                borderRadius: '6px',
                fontSize: '14px',
                fontFamily: 'var(--font-family)',
              }}
            />
          </div>
          <button
            style={{
              padding: '8px 16px',
              backgroundColor: 'var(--color-magenta)',
              color: 'var(--color-white)',
              border: 'none',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: 500,
              cursor: 'pointer',
              fontFamily: 'var(--font-family)',
            }}
          >
            Save
          </button>
        </div>
      </div>

      {/* Maintenance windows */}
      <div style={{ marginBottom: '32px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h3 style={{ fontSize: '16px', fontWeight: 600, margin: 0, color: 'var(--color-black)' }}>
            Maintenance windows
          </h3>
          <button
            onClick={() => setAddWindowOpen(true)}
            style={{
              padding: '8px 16px',
              backgroundColor: 'var(--color-magenta)',
              color: 'var(--color-white)',
              border: 'none',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: 500,
              cursor: 'pointer',
              fontFamily: 'var(--font-family)',
            }}
          >
            + Add window
          </button>
        </div>

        <DataTable
          columns={[
            { key: 'description', header: 'Description', render: (value: string) => <div style={{ fontWeight: 500 }}>{value}</div> },
            { key: 'startDate', header: 'Start date', render: (value: string) => formatDate(value) },
            { key: 'endDate', header: 'End date', render: (value: string) => formatDate(value) },
          ]}
          data={maintenanceWindows}
          rowKey="description"
          actions={[
            { label: 'Edit', onClick: (_row: any) => {} },
            { label: 'Delete', onClick: (_row: any) => {}, variant: 'danger' as const },
          ]}
        />
      </div>

      {/* Delivery logs */}
      <div>
        <h3 style={{ fontSize: '16px', fontWeight: 600, margin: '0 0 16px 0', color: 'var(--color-black)' }}>
          Delivery logs
        </h3>

        <DataTable
          columns={[
            { key: 'recipient', header: 'Recipient', render: (value: string) => value },
            { key: 'subject', header: 'Subject', render: (value: string) => <div style={{ fontWeight: 500 }}>{value}</div> },
            {
              key: 'status',
              header: 'Status',
              render: (value: string) => (
                <span
                  style={{
                    display: 'inline-block',
                    padding: '4px 10px',
                    borderRadius: '4px',
                    fontSize: '12px',
                    fontWeight: 600,
                    backgroundColor: value === 'Delivered' ? '#dcfce7' : value === 'Failed' ? '#fecaca' : '#fef3c7',
                    color: value === 'Delivered' ? '#15803d' : value === 'Failed' ? '#dc2626' : '#b45309',
                  }}
                >
                  {value}
                </span>
              ),
            },
            { key: 'timestamp', header: 'Timestamp', render: (value: string) => formatDateTime(value) },
          ]}
          data={deliveryLogs}
          rowKey="timestamp"
        />
      </div>

      {/* Add maintenance window modal */}
      <Modal
          isOpen={addWindowOpen}
          title="Add maintenance window"
          onClose={() => setAddWindowOpen(false)}
          isDirty={newWindow.startDate !== '' || newWindow.endDate !== '' || newWindow.description !== ''}
          footer={
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setAddWindowOpen(false)}
                style={{
                  padding: '8px 16px',
                  border: '1px solid var(--color-light-grey)',
                  borderRadius: '6px',
                  backgroundColor: 'var(--color-white)',
                  cursor: 'pointer',
                  fontFamily: 'var(--font-family)',
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleAddMaintenanceWindow}
                style={{
                  padding: '8px 16px',
                  backgroundColor: 'var(--color-magenta)',
                  color: 'var(--color-white)',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontFamily: 'var(--font-family)',
                }}
              >
                Add
              </button>
            </div>
          }
        >
          <div style={{ display: 'grid', gap: '16px' }}>
            <div>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, marginBottom: '4px' }}>
                Description
              </label>
              <input
                type="text"
                value={newWindow.description}
                onChange={(e) => setNewWindow({ ...newWindow, description: e.target.value })}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid var(--color-light-grey)',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontFamily: 'var(--font-family)',
                }}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, marginBottom: '4px' }}>
                Start date
              </label>
              <input
                type="date"
                value={newWindow.startDate}
                onChange={(e) => setNewWindow({ ...newWindow, startDate: e.target.value })}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid var(--color-light-grey)',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontFamily: 'var(--font-family)',
                }}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, marginBottom: '4px' }}>
                End date
              </label>
              <input
                type="date"
                value={newWindow.endDate}
                onChange={(e) => setNewWindow({ ...newWindow, endDate: e.target.value })}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid var(--color-light-grey)',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontFamily: 'var(--font-family)',
                }}
              />
            </div>
          </div>
        </Modal>
    </div>
  )
}

export default NotificationSettings
